import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
    Assessment as AssessmentIcon,
    Assignment as AssignmentIcon,
    Build as BuildIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import SessionActionsDialog from './SessionActionsDialog';
import SessionFeedbackDialog from './SessionFeedbackDialog';
import SessionReportDialog from './SessionReportDialog';
import { InterviewSession } from './types';

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'error'> = {
    pending: 'default',
    in_progress: 'info',
    completed: 'success',
    cancelled: 'error',
};

const STICKY_ID_WIDTH = 72;
const stickyIdCellSx = {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    minWidth: STICKY_ID_WIDTH,
    maxWidth: STICKY_ID_WIDTH,
    width: STICKY_ID_WIDTH,
    backgroundColor: 'var(--bg-surface)',
    'tr.MuiTableRow-hover:hover > &': { backgroundColor: 'action.hover' },
} as const;
const stickyCandidateCellSx = {
    position: 'sticky',
    left: STICKY_ID_WIDTH,
    zIndex: 2,
    minWidth: 200,
    width: 200,
    backgroundColor: 'var(--bg-surface)',
    'tr.MuiTableRow-hover:hover > &': { backgroundColor: 'action.hover' },
} as const;
const stickyIdHeadSx = {
    ...stickyIdCellSx,
    zIndex: 3,
    backgroundColor: 'var(--neutral-50)',
    'tr.MuiTableRow-hover:hover > &': { backgroundColor: 'var(--neutral-50)' },
} as const;
const stickyCandidateHeadSx = {
    ...stickyCandidateCellSx,
    zIndex: 3,
    backgroundColor: 'var(--neutral-50)',
    'tr.MuiTableRow-hover:hover > &': { backgroundColor: 'var(--neutral-50)' },
} as const;

interface Notification {
    message: string;
    severity: 'success' | 'error';
}

// Only fields the backend lists in ordering_fields.
type SortField = 'id' | 'status' | 'started_at' | 'effective_score';

const InterviewSessions: React.FC = () => {
    const [sessions, setSessions] = useState<InterviewSession[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('completed');
    const [healthFilter, setHealthFilter] = useState<string>('all');
    const [orderBy, setOrderBy] = useState<SortField>('started_at');
    const [orderDir, setOrderDir] = useState<'asc' | 'desc'>('desc');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [selectedSession, setSelectedSession] = useState<InterviewSession | null>(null);
    const [notification, setNotification] = useState<Notification | null>(null);

    const fetchSessions = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                page: (page + 1).toString(),
                page_size: rowsPerPage.toString(),
            });

            if (searchTerm) {
                params.append('search', searchTerm);
            }
            if (statusFilter !== 'all') {
                params.append('status', statusFilter);
            }
            // Company-managed candidate interviews stay on Company hub.
            // scope=individual also includes practice sessions, which are
            // stored under a company and would be hidden by company__isnull.
            params.append('scope', 'individual');

            // The trailing -id is a tiebreaker. Many sessions share a status,
            // and paginating a non-unique sort order lets rows repeat or
            // vanish between pages.
            const prefix = orderDir === 'desc' ? '-' : '';
            params.append(
                'ordering',
                orderBy === 'id' ? `${prefix}id` : `${prefix}${orderBy},-id`,
            );

            const response = await api.get(`/super-admin/interview-sessions/?${params}`);
            setSessions(response.data.results || response.data);
            setTotalCount(response.data.count || (response.data.results || response.data).length);
        } catch (error: any) {
            console.error('Error fetching interview sessions:', error);
            setError(
                error.response?.data?.error
                || error.message
                || 'Failed to fetch interview sessions',
            );
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, searchTerm, statusFilter, orderBy, orderDir]);

    useEffect(() => {
        const timer = setTimeout(fetchSessions, searchTerm ? 400 : 0);
        return () => clearTimeout(timer);
    }, [fetchSessions, searchTerm]);

    const handleSort = (field: SortField) => {
        if (orderBy === field) {
            setOrderDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setOrderBy(field);
            // Dates and scores are most useful highest-first; names and ids A–Z.
            setOrderDir(field === 'started_at' || field === 'effective_score' ? 'desc' : 'asc');
        }
        setPage(0);
    };

    const sortableHeader = (field: SortField, label: string, sx?: object) => (
        <TableCell sortDirection={orderBy === field ? orderDir : false} sx={sx}>
            <TableSortLabel
                active={orderBy === field}
                direction={orderBy === field ? orderDir : 'asc'}
                onClick={() => handleSort(field)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    // Health is derived per row, so it is filtered client-side on the current
    // page rather than added as a backend query parameter.
    const visibleSessions = sessions.filter((session) => {
        if (healthFilter === 'all') return true;
        if (healthFilter === 'no_recording') {
            return !session.has_session_recording && session.question_video_count > 0;
        }
        if (healthFilter === 'no_feedback') return !session.has_feedback;
        if (healthFilter === 'unscored') return session.unscored_question_count > 0;
        return true;
    });

    const handleOpenActions = (session: InterviewSession) => {
        setSelectedSession(session);
        setDialogOpen(true);
    };

    const handleOpenReport = (session: InterviewSession) => {
        setSelectedSession(session);
        setReportOpen(true);
    };

    const handleOpenFeedback = (session: InterviewSession) => {
        setSelectedSession(session);
        setFeedbackOpen(true);
    };

    const handleSessionChanged = useCallback(async () => {
        if (!selectedSession) return;
        // Refetch just the open row so its health chips stay accurate without
        // reshuffling the list under the user.
        try {
            const response = await api.get(
                `/super-admin/interview-sessions/${selectedSession.id}/`,
            );
            const updated: InterviewSession = response.data;
            setSelectedSession(updated);
            setSessions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
        } catch (error: any) {
            console.error('Error refreshing interview session:', error);
        }
    }, [selectedSession]);

    const renderHealth = (session: InterviewSession) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {session.question_video_count > 0 && !session.has_session_recording && (
                <Chip size="small" color="warning" label="No full recording" />
            )}
            {!session.has_feedback && (
                <Chip size="small" color="warning" label="No report" />
            )}
            {session.unscored_question_count > 0 && (
                <Chip
                    size="small"
                    color="error"
                    label={`${session.unscored_question_count} unscored`}
                />
            )}
            {session.has_session_recording
                && session.has_feedback
                && session.unscored_question_count === 0 && (
                <Chip size="small" color="success" label="Healthy" />
            )}
        </Box>
    );

    if (loading && sessions.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
                    Individual Interviews
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Recover interviews whose recording or AI evaluation did not complete, for
                    people who booked directly: practice interviews and other individual-user
                    sessions. Company-managed candidate interviews stay on the Interviews tab in
                    Company hub.
                </Typography>
            </Box>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                        <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                            <TextField
                                fullWidth
                                placeholder="Search by candidate or job role..."
                                value={searchTerm}
                                onChange={(event) => {
                                    setSearchTerm(event.target.value);
                                    setPage(0);
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <Box sx={{ flex: '0 1 200px', minWidth: '180px' }}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label="Status"
                                    onChange={(event: SelectChangeEvent) => {
                                        setStatusFilter(event.target.value);
                                        setPage(0);
                                    }}
                                >
                                    <MenuItem value="all">All statuses</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                    <MenuItem value="cancelled">Cancelled</MenuItem>
                                    <MenuItem value="in_progress">In progress</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ flex: '0 1 220px', minWidth: '200px' }}>
                            <FormControl fullWidth>
                                <InputLabel>Health</InputLabel>
                                <Select
                                    value={healthFilter}
                                    label="Health"
                                    onChange={(event: SelectChangeEvent) => setHealthFilter(event.target.value)}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="no_recording">Missing full recording</MenuItem>
                                    <MenuItem value="no_feedback">Missing report</MenuItem>
                                    <MenuItem value="unscored">Has unscored answers</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box>
                            <Tooltip title="Refresh">
                                <IconButton onClick={fetchSessions} color="primary">
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    {healthFilter !== 'all' && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                            The health filter applies to this page of results only.
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Card>
                <TableContainer
                    sx={{
                        '&&': { overflowX: 'auto !important', overflowY: 'hidden !important' },
                        scrollbarWidth: 'thin',
                    }}
                >
                    <Table sx={{ minWidth: 1000, borderCollapse: 'separate', borderSpacing: 0 }}>
                        <TableHead>
                            <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                                {sortableHeader('id', 'ID', stickyIdHeadSx)}
                                <TableCell sx={stickyCandidateHeadSx}>Candidate</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Company</TableCell>
                                {sortableHeader('status', 'Status')}
                                {sortableHeader('started_at', 'Started')}
                                {sortableHeader('effective_score', 'Score')}
                                <TableCell>Pipeline health</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleSessions.map((session) => (
                                <TableRow key={session.id} hover>
                                    <TableCell sx={stickyIdCellSx}>
                                        <Typography variant="body2" fontWeight="medium">
                                            {session.id}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={stickyCandidateCellSx}>
                                        <Typography variant="body2">
                                            {session.candidate_name || '—'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {session.candidate_email || 'No email'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {session.job_description_title || session.job_role}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {session.company_name || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={session.status.replace('_', ' ')}
                                            color={STATUS_COLORS[session.status] || 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {session.started_at
                                                ? new Date(session.started_at).toLocaleString()
                                                : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {session.effective_score != null
                                                ? `${session.effective_score.toFixed(1)}%`
                                                : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{renderHealth(session)}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip
                                            title={
                                                session.has_feedback
                                                    ? 'View assessment report'
                                                    : 'No report generated yet'
                                            }
                                        >
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    disabled={!session.has_feedback}
                                                    onClick={() => handleOpenReport(session)}
                                                >
                                                    <AssessmentIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Post-interview feedback">
                                            <IconButton
                                                size="small"
                                                color="success"
                                                onClick={() => handleOpenFeedback(session)}
                                            >
                                                <AssignmentIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Recovery actions">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleOpenActions(session)}
                                            >
                                                <BuildIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {visibleSessions.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            align="center"
                                            sx={{ py: 3 }}
                                        >
                                            No interview sessions match these filters.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(_event, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </Card>

            <SessionActionsDialog
                open={dialogOpen}
                session={selectedSession}
                onClose={() => setDialogOpen(false)}
                onSessionChanged={handleSessionChanged}
                onNotify={(message, severity) => setNotification({ message, severity })}
            />

            <SessionReportDialog
                open={reportOpen}
                session={selectedSession}
                onClose={() => setReportOpen(false)}
            />

            <SessionFeedbackDialog
                open={feedbackOpen}
                session={selectedSession}
                onClose={() => setFeedbackOpen(false)}
            />

            <Snackbar
                open={notification !== null}
                autoHideDuration={6000}
                onClose={() => setNotification(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={notification?.severity || 'success'}
                    onClose={() => setNotification(null)}
                    variant="filled"
                >
                    {notification?.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default InterviewSessions;
